// @givmo/assistant-panel — the panel's FRAME: its title, its close, and where it sits.
//
// ══ TWO PLACES, ONE PANEL ══════════════════════════════════════════════════════════════════════
//
// On a wide screen (the console's desktop layout, `lg` up) the panel is DOCKED: a full-height column at
// the right edge, beside the page and its top bar, whose width the shell reserves so both reflow into
// what is left (the console's assistant control publishes the width through `@givmo/org-console`'s
// `setConsoleAssistantDock`; the shell reads `useConsoleAssistantDock`). Nothing covers the page and
// nothing covers the top bar's cluster, so a panel that opens by default (the email editor) never hides
// the account menu, the entry control or the page's own actions. Its header is the top bar's height, so
// the two rules line up. On a narrower screen there is no room beside the page, and the panel is the right-hand
// SHEET it always was — non-modal, over the page.
//
// ⚠ The sheet keeps `disablePointerDismissal` with `modal={false}`: Base UI closes a non-modal dialog
// when focus leaves it, so without the pair clicking into the very field the panel just wrote to would
// close it. The docked column is not a dialog at all — it is a complementary landmark beside `main`, and
// only its close button, Escape inside it, or the entry control closes it.
//
// ⚠ The docked column is PORTALLED to the end of `<body>`. The control that opens it lives in the top
// bar, and a column drawn there would sit inside the banner landmark and in the middle of the Tab
// order — the entry control, then the whole panel, then the rest of the top bar, then the page. At the
// end of the document it is a top-level complementary landmark and comes after the page in Tab order.
// It takes focus only when the person opened it (`focusOnOpen`), never when a page opens it by default:
// a page that loads must not have its focus pulled into a side panel. Handing focus BACK when it closes
// is the host's (`ConsoleAssistant`), which holds the entry control.
//
// ⚠ The sheet still covers the right of the top bar, including the control that opened it — every
// Sheet in the suite does, and Escape and the header's close both dismiss it.

import { useEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon, IconButton, Sheet, testIdProp } from '@givmo/design-system';
import { X } from 'lucide-react';

/**
 * The docked column's width: 360px in the desktop layout, 400px from `xl`, so the page beside it keeps
 * room on a smaller laptop. The shell reserves exactly what the control publishes (`dockWidth`).
 */
export const ASSISTANT_DOCK_WIDTH = { base: 360, xl: 400 } as const;

/** The class string for those widths — full literals, so Tailwind emits them. */
const DOCK_WIDTH_CLASS = 'w-[360px] xl:w-[400px]';

export interface AssistantPanelFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Beside the page and its top bar (`true`), or the right-hand sheet over it. */
  docked: boolean;
  /** The panel's title — the assistant's name — and its accessible name. */
  title: string;
  testId: string;
  /** What to focus when the SHEET opens (a docked column takes no focus on its own: it may open by
   *  default, and a page that loads must not have its focus pulled into a side panel). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Docked only: move focus into the column as it opens — the person asked for it. Without
   *  `initialFocus`, the first text field inside it (a page-drawn body's composer). */
  focusOnOpen?: boolean;
  /** The body (and footer) regions, as `Sheet.Body` / `Sheet.Footer` draw them. */
  children: ReactNode;
}

export function AssistantPanelFrame({
  open,
  onOpenChange,
  docked,
  title,
  testId,
  initialFocus,
  focusOnOpen = false,
  children,
}: AssistantPanelFrameProps) {
  const column = useRef<HTMLElement>(null);
  const showsDocked = docked && open;
  useEffect(() => {
    if (!showsDocked || !focusOnOpen) return;
    // A page-drawn body is portalled in after the column mounts, so look again on the next frame.
    const focusInside = (): boolean => {
      const target =
        initialFocus?.current ??
        column.current?.querySelector<HTMLElement>('textarea, input:not([type="hidden"])') ??
        null;
      if (target === null) return false;
      target.focus();
      return true;
    };
    if (focusInside()) return;
    const frame = requestAnimationFrame(() => {
      focusInside();
    });
    return () => cancelAnimationFrame(frame);
  }, [showsDocked, focusOnOpen, initialFocus]);

  if (docked) {
    if (!open || typeof document === 'undefined') return null;
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      // Escape closes it, as it closes the sheet — unless something inside (a menu, a popover)
      // already took the key.
      if (event.key !== 'Escape' || event.defaultPrevented || event.nativeEvent.isComposing) return;
      event.preventDefault();
      onOpenChange(false);
    };
    return createPortal(
      <aside
        ref={column}
        aria-label={title}
        onKeyDown={onKeyDown}
        className={`fixed top-0 right-0 bottom-0 z-20 flex ${DOCK_WIDTH_CLASS} flex-col border-l border-border-subtle bg-surface`}
        {...testIdProp(testId)}
        data-presentation="docked"
      >
        <div className="flex h-[56px] shrink-0 items-center justify-between gap-16 border-b border-border-subtle px-20">
          <h2 className="min-w-0 truncate font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {title}
          </h2>
          <IconButton
            size="sm"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
            testId={`${testId}-close`}
          >
            <Icon icon={X} size={18} aria-hidden />
          </IconButton>
        </div>
        {children}
      </aside>,
      document.body,
    );
  }
  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange} modal={false} disablePointerDismissal>
      <Sheet.Content
        side="right"
        size="md"
        testId={testId}
        initialFocus={initialFocus}
        aria-label={title}
      >
        <Sheet.Header title={title} />
        {children}
      </Sheet.Content>
    </Sheet.Root>
  );
}
